import { fetchSuggestions } from '@lib/googlePlaces'
import {
	PlaceSearchContext,
	PlaceSearchFor,
	placeSearchContextOf,
	placeSearchScopeFor,
} from '@lib/placeSearch'
import { queryKeys } from '@lib/queryKeys'
import { useQuery } from '@tanstack/react-query'
import { useEvents } from './useEvents'
import { useIdeas } from './useIdeas'
import { useTrip } from './useTrip'

/**
 * A destination's Suggestions, kept for as long as the tab is open — Google's
 * terms allow no longer, and a destination's top sights don't change over a
 * planning session. A failed lookup isn't kept or retried: the section is
 * simply absent, and the next search that opens tries again.
 */
export const placeSuggestionsQueryOptions = (placeName: string | null) => ({
	queryKey: queryKeys.placeSuggestions(placeName),
	queryFn: async () => {
		if (!placeName) throw new Error('A place name is required')
		return fetchSuggestions(placeName)
	},
	enabled: placeName !== null,
	staleTime: Infinity,
	gcTime: Infinity,
	retry: false,
})

/**
 * What a form's Place search knows of its trip (`LocationAutocomplete`'s
 * `context`): the destination it's for, the Trip places from the trip's ideas
 * and events, and that destination's Suggestions, fetched on first use.
 */
export const usePlaceSearchContext = (
	tripId: string | undefined,
	searchFor: PlaceSearchFor,
): PlaceSearchContext => {
	const { data: trip } = useTrip(tripId)
	// An accommodation's search offers no Trip places, so it loads none.
	const tripPlacesTripId = searchFor.kind === 'accommodation' ? undefined : tripId
	const { data: ideasData } = useIdeas(tripPlacesTripId)
	const { data: eventsData } = useEvents(tripPlacesTripId)
	const placeName = placeSearchScopeFor(searchFor, trip)?.suggestionsPlaceName ?? null
	const { data: suggestions } = useQuery(placeSuggestionsQueryOptions(placeName))

	return placeSearchContextOf(
		searchFor,
		trip,
		{ ideas: ideasData?.ideas ?? [], events: eventsData?.events ?? [] },
		suggestions ?? [],
	)
}
